import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";

const Row = ({ r }) => (
  <tr className="border-b border-zinc-900" data-testid={`licence-row-${r.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
    <td className="px-4 py-4 text-sm text-white">
      {r.description}
      <span className="mono mt-1 block text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">
        {(r.paths?.length ? r.paths : [r.path]).filter(Boolean).join(" · ")}
      </span>
    </td>
    <td className="px-4 py-4 text-xs text-zinc-300">{r.kind}</td>
    <td className="px-4 py-4 text-xs text-zinc-300">{r.author || <span className="text-maasai">Not recorded</span>}</td>
    <td className="px-4 py-4 text-xs text-zinc-300">
      {r.licence_detail?.name ? (
        r.licence_detail.url
          ? <a href={r.licence_detail.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-ochre hover:underline">
              {r.licence_detail.name} <ExternalLink size={11} />
            </a>
          : r.licence_detail.name
      ) : <span className="text-maasai">Not recorded</span>}
    </td>
    <td className="px-4 py-4 text-xs">
      {r.found_at
        ? <a href={r.found_at} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-ochre">Found here — not a licence link</a>
        : r.source_url
        ? <a href={r.source_url} target="_blank" rel="noreferrer" className="text-zinc-300 hover:text-ochre">Source file</a>
        : <span className="text-zinc-600">—</span>}
    </td>
    <td className="px-4 py-4">
      {r.complete
        ? <span className="mono inline-flex items-center gap-1 text-[0.55rem] uppercase tracking-[0.14em] text-ochre"><Check size={12} /> Complete</span>
        : <span className="mono inline-flex items-center gap-1 text-[0.55rem] uppercase tracking-[0.14em] text-maasai"><AlertTriangle size={12} /> Incomplete</span>}
    </td>
  </tr>
);

const Table = ({ rows, testid }) => (
  <div className="mt-6 overflow-x-auto border border-zinc-900">
    <table className="w-full min-w-[900px] text-left" data-testid={testid}>
      <thead>
        <tr className="border-b border-white/15 bg-[#0b0b0b]">
          {["What it is", "Kind", "Who made it", "Licence", "Source", "Record"].map((h) => (
            <th key={h} className="mono px-4 py-3 text-[0.52rem] uppercase tracking-[0.16em] text-zinc-400">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{rows.map((r) => <Row key={r.id} r={r} />)}</tbody>
    </table>
  </div>
);

/** Every image and mark on the site, the terms it is used under, and an honest count of what is not recorded. */
export default function Licences() {
  const [d, setD] = useState(null);

  useSeo({
    title: "Licence And Source Register | KickStartKenyaFootball",
    description:
      "Every photograph, brand mark and club badge published on this site, who made it, the licence it is used under and a link to the original — with any incomplete attribution listed rather than hidden.",
  });

  useEffect(() => {
    api.get("/licences").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);

  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="licences-loading" />;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="licences-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">Transparency and legal</p>
          <h1 className="display-1 mt-5 max-w-4xl">{d.heading}</h1>
          <p className="body-copy mt-6 max-w-3xl">{d.intro}</p>
        </Reveal>

        <div className="mt-10 grid gap-px bg-zinc-800 sm:grid-cols-3" data-testid="licences-totals">
          {[["Images and marks published", d.totals.items],
            ["With a complete record", d.totals.recorded],
            ["Attribution incomplete", d.totals.gaps]].map(([label, value], i) => (
            <div key={label} className="bg-[#0b0b0b] p-6">
              <p className="mono text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400">{label}</p>
              <p className={`mt-3 font-display text-3xl font-bold leading-none ${i === 2 && value > 0 ? "text-maasai" : "text-white"}`}>
                {value}
              </p>
            </div>
          ))}
        </div>

        <p className="mono mt-8 max-w-3xl border-l-2 border-ochre bg-[#0b0b0b] p-6 text-[0.58rem] uppercase leading-relaxed tracking-[0.13em] text-zinc-300"
          data-testid="licences-cc-rule">
          {d.cc_rule}
        </p>

        {d.gaps?.length ? (
          <section className="mt-14" data-testid="licences-gaps">
            <h2 className="display-3">What Is Not Yet Recorded</h2>
            <p className="body-copy mt-5 max-w-3xl">{d.gap_note}</p>
            <ul className="mt-6 space-y-px bg-zinc-800">
              {d.gaps.map((g) => (
                <li key={g.id} className="grid gap-2 bg-[#0b0b0b] p-5 md:grid-cols-[2fr_1fr]"
                  data-testid={`licence-gap-${g.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <p className="text-sm text-white">{g.description}</p>
                  <p className="mono text-[0.58rem] uppercase tracking-[0.14em] text-maasai">Missing: {g.missing}</p>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="mt-16">
          <h2 className="display-3">Player Photographs</h2>
          <Table rows={d.portraits} testid="licences-portraits-table" />
        </section>

        <section className="mt-16">
          <h2 className="display-3">Brand Marks And Club Badges</h2>
          <Table rows={d.assets} testid="licences-assets-table" />
        </section>

        <section className="mt-16">
          <h2 className="display-3">The Licences Themselves</h2>
          <div className="mt-6 grid gap-px bg-zinc-800 md:grid-cols-2" data-testid="licences-catalogue">
            {d.licences.map((l) => (
              <div key={l.id} className="bg-[#0b0b0b] p-6">
                <p className="text-sm font-semibold text-white">{l.name}</p>
                <p className="mt-2 text-xs leading-relaxed text-zinc-400">{l.requires}</p>
                {l.url ? (
                  <a href={l.url} target="_blank" rel="noreferrer" className="mono mt-3 inline-flex items-center gap-1 text-[0.55rem] uppercase tracking-[0.14em] text-ochre hover:underline">
                    Read the licence <ExternalLink size={11} />
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        </section>

        <p className="mt-14 max-w-3xl text-sm leading-relaxed text-zinc-400" data-testid="licences-statistics-note">
          {d.statistics_note}{" "}
          <Link to={d.statistics_link} className="text-ochre underline">Sources and method</Link>.
        </p>
      </div>
    </div>
  );
}
